// Maya Premium owner/admin access module.
//
// - getMyMayaAccess: signed-in user's effective access (owner bypass, grants, purchases).
// - getGuestMayaAccess: guest visitor access by email.
// - Admin (owner|admin) only:
//     listMayaGrants, createMayaGrant, revokeMayaGrant,
//     searchMayaUsers, getMayaOwnerMetrics.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/lib/require-auth";
import { userHasAnyRole, userHasRole } from "@/lib/has-role.server";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

type AccessSource = "owner" | "grant" | "purchase" | null;

export interface MayaAccessResult {
  hasAccess: boolean;
  isOwner: boolean;
  hasPass: boolean;
  isUnlimited: boolean;
  destinations: string[];
  source: AccessSource;
  expiresAt: string | null;
  email: string | null;
  authenticated: boolean;
}

async function assertOwnerOrAdmin(ctx: {
  supabase: SupabaseClient<Database>;
  userId: string;
}) {
  const ok = await userHasAnyRole(ctx.supabase, ctx.userId, ["admin", "owner"]);
  if (!ok) throw new Error("Forbidden");
}

async function computeAccessViaRpc(
  sb: SupabaseClient<Database>,
  args: {
    userId: string | null;
    email: string | null;
    destinationKey: string | null;
    tourSlug?: string | null;
  },
): Promise<{
  hasAccess: boolean;
  source: AccessSource;
  isOwner: boolean;
  hasPass: boolean;
  isUnlimited: boolean;
  expiresAt: string | null;
}> {
  // 4-arg overload (accepts tour_slug); the RPC returns a single-row table.
  const { data, error } = await sb.rpc("has_maya_access", {
    _user_id: args.userId,
    _email: args.email ?? "",
    _destination_key: args.destinationKey,
    _tour_slug: args.tourSlug ?? null,
  } as never);

  if (error || !data || !data.length) {
    return {
      hasAccess: false,
      source: null,
      isOwner: false,
      hasPass: false,
      isUnlimited: false,
      expiresAt: null,
    };
  }
  const row = data[0] as {
    has_access: boolean;
    source: AccessSource;
    is_owner: boolean;
    has_pass: boolean;
    is_unlimited: boolean;
    expires_at: string | null;
  };
  return {
    hasAccess: !!row.has_access,
    source: row.source ?? null,
    isOwner: !!row.is_owner,
    hasPass: !!row.has_pass,
    isUnlimited: !!row.is_unlimited,
    expiresAt: row.expires_at ?? null,
  };
}

async function loadDestinations(
  sb: SupabaseClient<Database>,
  args: { userId: string | null; email: string | null },
): Promise<string[]> {
  const destinations = new Set<string>();
  // grants
  let grantsQ = sb
    .from("maya_access_grants")
    .select("destination_keys, access_type, revoked_at, expires_at, user_id, email");
  if (args.userId) {
    grantsQ = grantsQ.or(
      `user_id.eq.${args.userId}${args.email ? `,email.eq.${args.email}` : ""}`,
    );
  } else if (args.email) {
    grantsQ = grantsQ.eq("email", args.email);
  } else {
    return [];
  }
  const { data: grants } = await grantsQ;
  (grants ?? []).forEach((g: any) => {
    if (g.revoked_at) return;
    if (g.expires_at && new Date(g.expires_at) <= new Date()) return;
    (g.destination_keys ?? []).forEach((d: string) => destinations.add(d));
  });
  // purchases
  let purchasesQ = sb
    .from("maya_purchases")
    .select("destination_key, product_id, status")
    .eq("status", "paid")
    .eq("product_id", "maya_destination_one_time");
  if (args.userId) {
    purchasesQ = purchasesQ.or(
      `user_id.eq.${args.userId}${args.email ? `,customer_email.ilike.${args.email}` : ""}`,
    );
  } else if (args.email) {
    purchasesQ = purchasesQ.ilike("customer_email", args.email);
  }
  const { data: purchases } = await purchasesQ;
  (purchases ?? []).forEach((p: any) => {
    if (p.destination_key) destinations.add(p.destination_key);
  });
  return [...destinations];
}

// ────────────────────────────────────────────────────────────────────────────
// Public: signed-in user's effective access.
// ────────────────────────────────────────────────────────────────────────────
export const getMyMayaAccess = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        destinationKey: z.string().trim().min(1).max(160).optional(),
        tourSlug: z.string().trim().min(1).max(160).optional(),
      })
      .parse(d ?? {}),
  )
  .handler(async ({ data, context }): Promise<MayaAccessResult> => {
    const sb = context.supabase;
    const email = ((context.claims?.email as string | undefined) ?? "").toLowerCase() || null;
    const userId = context.userId;
    const access = await computeAccessViaRpc(sb, {
      userId,
      email,
      destinationKey: data.destinationKey ?? null,
      tourSlug: data.tourSlug ?? null,
    });
    const destinations = access.isUnlimited
      ? []
      : await loadDestinations(sb, { userId, email });
    return {
      ...access,
      destinations,
      email,
      authenticated: true,
    };
  });

// ────────────────────────────────────────────────────────────────────────────
// Public (unauth): guest access by remembered email — used by paywalls and the
// audio guide before the visitor signs in.
// ────────────────────────────────────────────────────────────────────────────
export const getGuestMayaAccess = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        destinationKey: z.string().trim().min(1).max(160).optional(),
        tourSlug: z.string().trim().min(1).max(160).optional(),
        email: z.string().trim().email().max(255).optional(),
      })
      .parse(d ?? {}),
  )
  .handler(async ({ data }): Promise<MayaAccessResult> => {
    // Per-IP rate limit — prevents email enumeration by scripted callers.
    try {
      const { getRequest, setResponseStatus } = await import("@tanstack/react-start/server");
      const { getClientIp, rateLimit } = await import("@/lib/ai-rate-limit.server");
      const req = getRequest();
      if (req) {
        const ip = getClientIp(req);
        const rl = rateLimit(`maya-access:${ip}`, { windowMs: 60_000, max: 20 });
        if (!rl.ok) {
          setResponseStatus(429);
          return {
            hasAccess: false,
            isOwner: false,
            hasPass: false,
            isUnlimited: false,
            destinations: [],
            source: null,
            expiresAt: null,
            email: null,
            authenticated: false,
          };
        }
      }
    } catch {
      /* rate limit best-effort; never block on infra glitch */
    }

    const email = (data.email ?? "").toLowerCase() || null;
    if (!email) {
      return {
        hasAccess: false,
        isOwner: false,
        hasPass: false,
        isUnlimited: false,
        destinations: [],
        source: null,
        expiresAt: null,
        email: null,
        authenticated: false,
      };
    }
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const access = await computeAccessViaRpc(supabaseAdmin, {
      userId: null,
      email,
      destinationKey: data.destinationKey ?? null,
      tourSlug: data.tourSlug ?? null,
    });
    // SECURITY: never return the full list of purchased destinations to an
    // unauthenticated caller — enumeration risk. Only the boolean for the
    // requested key is exposed.
    return {
      ...access,
      destinations: [],
      email,
      authenticated: false,
    };
  });


// ────────────────────────────────────────────────────────────────────────────
// Admin: grants management
// ────────────────────────────────────────────────────────────────────────────
const ACCESS_TYPES = [
  "destination",
  "multi_destination",
  "unlimited",
  "pass",
  "lifetime",
] as const;
type AccessType = (typeof ACCESS_TYPES)[number];

const EXPIRY_PRESETS = [
  "24h",
  "7d",
  "30d",
  "1y",
  "never",
] as const;
type ExpiryPreset = (typeof EXPIRY_PRESETS)[number];

function presetToTimestamp(preset: ExpiryPreset): string | null {
  if (preset === "never") return null;
  const now = Date.now();
  const ms =
    preset === "24h"
      ? 24 * 60 * 60 * 1000
      : preset === "7d"
        ? 7 * 24 * 60 * 60 * 1000
        : preset === "30d"
          ? 30 * 24 * 60 * 60 * 1000
          : 365 * 24 * 60 * 60 * 1000;
  return new Date(now + ms).toISOString();
}

export interface MayaGrantRow {
  id: string;
  email: string;
  user_id: string | null;
  access_type: AccessType;
  destination_keys: string[];
  expires_at: string | null;
  note: string | null;
  granted_by: string | null;
  granted_by_email: string | null;
  revoked_at: string | null;
  email_sent_at: string | null;
  created_at: string;
  active: boolean;
}

function isActive(row: { revoked_at: string | null; expires_at: string | null }) {
  if (row.revoked_at) return false;
  if (row.expires_at && new Date(row.expires_at) <= new Date()) return false;
  return true;
}

export const listMayaGrants = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        search: z.string().trim().max(120).optional(),
        status: z.enum(["all", "active", "expired", "revoked"]).default("all"),
        limit: z.number().int().min(1).max(200).default(100),
      })
      .parse(d ?? {}),
  )
  .handler(async ({ data, context }): Promise<MayaGrantRow[]> => {
    await assertOwnerOrAdmin(context as never);
    let q = context.supabase
      .from("maya_access_grants")
      .select(
        "id, email, user_id, access_type, destination_keys, expires_at, note, granted_by, granted_by_email, revoked_at, email_sent_at, created_at",
      )
      .order("created_at", { ascending: false })
      .limit(data.limit);
    if (data.search) {
      const s = data.search.toLowerCase();
      q = q.ilike("email", `%${s}%`);
    }
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    const mapped = (rows ?? []).map((r: unknown) => {
      const row = r as Omit<MayaGrantRow, "active">;
      return { ...row, active: isActive(row) } as MayaGrantRow;
    });
    if (data.status === "all") return mapped;
    return mapped.filter((r: MayaGrantRow) => {
      if (data.status === "active") return r.active;
      if (data.status === "revoked") return !!r.revoked_at;
      if (data.status === "expired")
        return !r.revoked_at && r.expires_at && new Date(r.expires_at) <= new Date();
      return true;
    });
  });

function generateInviteToken(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
}

export const createMayaGrant = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        email: z.string().trim().email().max(255),
        accessType: z.enum(ACCESS_TYPES),
        destinationKeys: z.array(z.string().trim().min(1).max(160)).max(50).default([]),
        expiresAt: z.enum(EXPIRY_PRESETS).default("never"),
        note: z.string().trim().max(2000).optional(),
        sendEmail: z.boolean().default(false),
        // Optional tour-specific fields — enable the tokenized "Regalar guía" flow.
        tourSlug: z.string().trim().min(1).max(200).optional(),
        tourTitle: z.string().trim().max(200).optional(),
        bokunProductId: z.string().trim().max(80).optional(),
        language: z.string().trim().min(2).max(5).optional(),
      })
      .parse(d),
  )
  .handler(
    async ({ data, context }): Promise<
      | { ok: true; id: string; token: string | null; emailSent: boolean; emailReason?: string }
      | { error: string }
    > => {
      await assertOwnerOrAdmin(context as never);
      const expiresAt = presetToTimestamp(data.expiresAt);
      const grantedByEmail = (context.claims?.email as string | undefined) ?? null;
      // Every courtesy grant now gets an opaque token so it can be delivered as
      // a per-recipient invite link — even multi_destination/pass gifts.
      const token = generateInviteToken();
      const tourSlugs = data.tourSlug ? [data.tourSlug] : [];

      const { data: row, error } = await context.supabase
        .from("maya_access_grants")
        .insert({
          email: data.email.toLowerCase(),
          access_type: data.accessType,
          destination_keys: data.destinationKeys,
          tour_slugs: tourSlugs,
          tour_title: data.tourTitle ?? null,
          bokun_product_id: data.bokunProductId ?? null,
          language: data.language ?? null,
          token,
          expires_at: expiresAt,
          note: data.note ?? null,
          granted_by: context.userId,
          granted_by_email: grantedByEmail,
        } as never)
        .select("id")
        .single();
      if (error) return { error: error.message };

      let emailSent = false;
      let emailReason: string | undefined;
      if (data.sendEmail) {
        try {
          const { sendMayaComplimentaryEmail } = await import("@/lib/maya-email.server");
          const res = await sendMayaComplimentaryEmail({
            customerEmail: data.email,
            accessType: data.accessType,
            destinationKeys: data.destinationKeys,
            expiresAt,
            grantedByEmail,
            token,
            tourSlug: data.tourSlug ?? null,
            tourTitle: data.tourTitle ?? null,
            lang: data.language,
          });
          if (res.ok) {
            emailSent = true;
            await context.supabase
              .from("maya_access_grants")
              .update({ email_sent_at: new Date().toISOString() })
              .eq("id", row.id);
          } else {
            emailReason = res.reason ?? "unknown";
          }
        } catch (err) {
          console.warn("[maya-grant] email failed", err);
          emailReason = (err as Error).message ?? "exception";
        }
      }
      return { ok: true, id: row.id as string, token, emailSent, emailReason };
    },
  );

export const revokeMayaGrant = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ id: z.string().uuid() }).parse(d),
  )
  .handler(async ({ data, context }): Promise<{ ok: true } | { error: string }> => {
    await assertOwnerOrAdmin(context as never);
    const { error } = await context.supabase
      .from("maya_access_grants")
      .update({ revoked_at: new Date().toISOString(), revoked_by: context.userId })
      .eq("id", data.id);
    if (error) return { error: error.message };
    return { ok: true };
  });

export const resendMayaGrantEmail = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(
    async ({ data, context }): Promise<
      { ok: true; emailSent: boolean; emailReason?: string } | { error: string }
    > => {
      await assertOwnerOrAdmin(context as never);
      const { data: row, error } = await context.supabase
        .from("maya_access_grants")
        .select(
          "id, email, access_type, destination_keys, tour_slugs, tour_title, language, token, expires_at, granted_by_email, revoked_at",
        )
        .eq("id", data.id)
        .maybeSingle();
      if (error) return { error: error.message };
      if (!row) return { error: "Grant no encontrado" };
      if (row.revoked_at) return { error: "Grant revocado — no se puede reenviar" };
      // Backfill token for pre-existing grants that were created before the
      // tokenized-invite migration shipped, so resends always yield a link.
      const anyRow = row as Record<string, unknown>;
      let token = (anyRow.token as string | null) ?? null;
      if (!token) {
        token = generateInviteToken();
        await context.supabase
          .from("maya_access_grants")
          .update({ token } as never)
          .eq("id", row.id);
      }
      try {
        const { sendMayaComplimentaryEmail } = await import("@/lib/maya-email.server");
        const tourSlugs = (anyRow.tour_slugs as string[] | null) ?? [];
        const res = await sendMayaComplimentaryEmail({
          customerEmail: row.email,
          accessType: row.access_type as (typeof ACCESS_TYPES)[number],
          destinationKeys: (row.destination_keys ?? []) as string[],
          expiresAt: row.expires_at,
          grantedByEmail: row.granted_by_email,
          token,
          tourSlug: tourSlugs[0] ?? null,
          tourTitle: (anyRow.tour_title as string | null) ?? null,
          lang: (anyRow.language as string | null) ?? undefined,
        });
        if (!res.ok) return { ok: true, emailSent: false, emailReason: res.reason ?? "unknown" };
        await context.supabase
          .from("maya_access_grants")
          .update({ email_sent_at: new Date().toISOString() })
          .eq("id", row.id);
        return { ok: true, emailSent: true };
      } catch (err) {
        console.warn("[maya-grant] resend failed", err);
        return { ok: true, emailSent: false, emailReason: (err as Error).message ?? "exception" };
      }
    },
  );

// ────────────────────────────────────────────────────────────────────────────
// Tokenized invites — per-tour "Regalar guía" links.
// ────────────────────────────────────────────────────────────────────────────
export interface MayaInvitePreview {
  status: "valid" | "revoked" | "expired" | "not_found";
  email: string | null;
  tourSlug: string | null;
  tourTitle: string | null;
  bokunProductId: string | null;
  language: string | null;
  accessType: string | null;
  expiresAt: string | null;
  acceptedAt: string | null;
  destinationKeys: string[];
}

async function resolveInviteRow(token: string) {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const sb = supabaseAdmin as unknown as {
    rpc: (name: string, args: Record<string, unknown>) => Promise<{ data: any; error: any }>;
  };
  const { data, error } = await sb.rpc("resolve_maya_invite", { _token: token });
  if (error || !data || !data.length) return null;
  return data[0] as {
    id: string;
    email: string;
    access_type: string;
    destination_keys: string[] | null;
    tour_slugs: string[] | null;
    tour_title: string | null;
    bokun_product_id: string | null;
    language: string | null;
    expires_at: string | null;
    accepted_at: string | null;
    revoked_at: string | null;
    granted_by_email: string | null;
    created_at: string;
  };
}

export const resolveMayaInvite = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z.object({ token: z.string().trim().min(16).max(128) }).parse(d),
  )
  .handler(async ({ data }): Promise<MayaInvitePreview> => {
    const row = await resolveInviteRow(data.token);
    if (!row) {
      return {
        status: "not_found",
        email: null,
        tourSlug: null,
        tourTitle: null,
        bokunProductId: null,
        language: null,
        accessType: null,
        expiresAt: null,
        acceptedAt: null,
        destinationKeys: [],
      };
    }
    let status: MayaInvitePreview["status"] = "valid";
    if (row.revoked_at) status = "revoked";
    else if (row.expires_at && new Date(row.expires_at) <= new Date()) status = "expired";
    const tourSlug = (row.tour_slugs ?? [])[0] ?? null;
    // Best-effort "opened" tracking — records last_opened_at even before
    // sign-in so admins can see when the recipient clicked the link.
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      await (supabaseAdmin as unknown as {
        rpc: (n: string, a: Record<string, unknown>) => Promise<unknown>;
      }).rpc("touch_maya_invite", { _token: data.token, _accept: false });
    } catch {
      /* tracking failure never blocks the invite */
    }
    return {
      status,
      email: row.email,
      tourSlug,
      tourTitle: row.tour_title,
      bokunProductId: row.bokun_product_id,
      language: row.language,
      accessType: row.access_type,
      expiresAt: row.expires_at,
      acceptedAt: row.accepted_at,
      destinationKeys: row.destination_keys ?? [],
    };
  });

export const acceptMayaInvite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ token: z.string().trim().min(16).max(128) }).parse(d),
  )
  .handler(
    async ({ data, context }): Promise<
      { ok: true; tourSlug: string | null; email: string; alreadyAccepted: boolean }
      | { error: string; code: "not_found" | "revoked" | "expired" | "email_mismatch" }
    > => {
      const row = await resolveInviteRow(data.token);
      if (!row) return { error: "Invitación no válida", code: "not_found" };
      if (row.revoked_at) return { error: "Esta invitación fue revocada", code: "revoked" };
      if (row.expires_at && new Date(row.expires_at) <= new Date())
        return { error: "Esta invitación expiró", code: "expired" };
      const callerEmail = ((context.claims?.email as string | undefined) ?? "").toLowerCase();
      if (!callerEmail || callerEmail !== row.email.toLowerCase())
        return {
          error: `Esta invitación es para ${row.email}. Inicia sesión con esa cuenta.`,
          code: "email_mismatch",
        };
      // Link the grant to this user on accept (idempotent).
      await context.supabase
        .from("maya_access_grants")
        .update({ user_id: context.userId } as never)
        .eq("id", row.id)
        .is("user_id", null);
      try {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        await (supabaseAdmin as unknown as {
          rpc: (n: string, a: Record<string, unknown>) => Promise<unknown>;
        }).rpc("touch_maya_invite", { _token: data.token, _accept: true });
      } catch {
        /* tracking failure never blocks accept */
      }
      return {
        ok: true,
        tourSlug: (row.tour_slugs ?? [])[0] ?? null,
        email: row.email,
        alreadyAccepted: !!row.accepted_at,
      };
    },
  );

// ────────────────────────────────────────────────────────────────────────────
// "Mis Guías" — biblioteca personal del usuario firmado.
// ────────────────────────────────────────────────────────────────────────────
export interface MyMayaGuideItem {
  id: string;
  source: "grant" | "purchase";
  tourSlug: string | null;
  tourTitle: string | null;
  destinationKeys: string[];
  language: string | null;
  accessType: string | null;
  status: "active" | "expired" | "revoked";
  expiresAt: string | null;
  grantedAt: string;
  acceptedAt: string | null;
  lastOpenedAt: string | null;
}

export const listMyMayaGuides = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<MyMayaGuideItem[]> => {
    const email = ((context.claims?.email as string | undefined) ?? "").toLowerCase() || null;
    const userId = context.userId;
    const sb = context.supabase;

    // Grants: everything owed to this user by email or user_id.
    let grantsQ = sb
      .from("maya_access_grants")
      .select(
        "id, email, user_id, access_type, destination_keys, tour_slugs, tour_title, language, expires_at, revoked_at, created_at, accepted_at, last_opened_at",
      )
      .order("created_at", { ascending: false });
    if (email) grantsQ = grantsQ.or(`user_id.eq.${userId},email.eq.${email}`);
    else grantsQ = grantsQ.eq("user_id", userId);
    const { data: grants } = await grantsQ;

    // Purchases: reveal per-tour and per-destination unlocks.
    let purchasesQ = sb
      .from("maya_purchases")
      .select(
        "id, product_id, destination_key, tour_slug, status, created_at, customer_email, user_id",
      )
      .order("created_at", { ascending: false });
    if (email) purchasesQ = purchasesQ.or(`user_id.eq.${userId},customer_email.eq.${email}`);
    else purchasesQ = purchasesQ.eq("user_id", userId);
    const { data: purchases } = await purchasesQ;

    const items: MyMayaGuideItem[] = [];
    (grants ?? []).forEach((g) => {
      const anyG = g as Record<string, unknown>;
      const revokedAt = anyG.revoked_at as string | null;
      const expiresAt = anyG.expires_at as string | null;
      const status: MyMayaGuideItem["status"] = revokedAt
        ? "revoked"
        : expiresAt && new Date(expiresAt) <= new Date()
          ? "expired"
          : "active";
      items.push({
        id: `grant:${anyG.id}`,
        source: "grant",
        tourSlug: ((anyG.tour_slugs as string[] | null) ?? [])[0] ?? null,
        tourTitle: (anyG.tour_title as string | null) ?? null,
        destinationKeys: ((anyG.destination_keys as string[] | null) ?? []),
        language: (anyG.language as string | null) ?? null,
        accessType: anyG.access_type as string,
        status,
        expiresAt,
        grantedAt: anyG.created_at as string,
        acceptedAt: (anyG.accepted_at as string | null) ?? null,
        lastOpenedAt: (anyG.last_opened_at as string | null) ?? null,
      });
    });
    (purchases ?? []).forEach((p) => {
      const anyP = p as Record<string, unknown>;
      if (anyP.status && anyP.status !== "paid") return;
      items.push({
        id: `purchase:${anyP.id}`,
        source: "purchase",
        tourSlug: (anyP.tour_slug as string | null) ?? null,
        tourTitle: null,
        destinationKeys: anyP.destination_key ? [anyP.destination_key as string] : [],
        language: null,
        accessType: (anyP.product_id as string) === "maya_pass_one_time" ? "pass" : "destination",
        status: "active",
        expiresAt: null,
        grantedAt: anyP.created_at as string,
        acceptedAt: null,
        lastOpenedAt: null,
      });
    });
    return items;
  });


// ────────────────────────────────────────────────────────────────────────────
// Admin: dashboard metrics
// ────────────────────────────────────────────────────────────────────────────
export interface MayaOwnerMetrics {
  totalUsers: number;
  premiumUsers: number;
  passUsers: number;
  manualAccessUsers: number;
  activeListeners: number;
  topDestinations: Array<{ destination: string; requests: number; purchases: number; revenue_cents: number }>;
  topPdfGuides: Array<{ destination: string; downloads: number }>;
  revenueCents: number;
}

export const getMayaOwnerMetrics = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<MayaOwnerMetrics> => {
    await assertOwnerOrAdmin(context as never);
    const sb = context.supabase;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const [rev, dest, convCount, purchaseRows, grantRows, listenerRows, pdfRows] =
      await Promise.all([
        supabaseAdmin.rpc("maya_revenue_summary"),
        supabaseAdmin.rpc("maya_top_destinations", { _limit: 10 }),
        sb.from("maya_conversations").select("*", { count: "exact", head: true }),
        sb
          .from("maya_purchases")
          .select("customer_email, product_id, status")
          .eq("status", "paid"),
        sb
          .from("maya_access_grants")
          .select("email, revoked_at, expires_at"),
        sb
          .from("maya_events")
          .select("user_id, metadata", { count: "exact" })
          .eq("event_type", "audio_preview_started")
          .gte("created_at", new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()),
        sb
          .from("maya_events")
          .select("destination, metadata")
          .eq("event_type", "pdf_guide_downloaded"),
      ]);

    const revenue = (rev.data?.[0] ?? { total_revenue_cents: 0 }) as {
      total_revenue_cents: number;
    };
    const purchaseEmails = new Set<string>();
    const passEmails = new Set<string>();
    (purchaseRows.data ?? []).forEach((r: { customer_email: string | null; product_id: string | null }) => {
      if (r.customer_email) {
        purchaseEmails.add(r.customer_email.toLowerCase());
        if (r.product_id === "maya_pass_one_time") passEmails.add(r.customer_email.toLowerCase());
      }
    });
    const manualEmails = new Set<string>();
    (grantRows.data ?? []).forEach((r: { email: string | null; revoked_at: string | null; expires_at: string | null }) => {
      if (
        r.email &&
        !r.revoked_at &&
        (!r.expires_at || new Date(r.expires_at) > new Date())
      ) {
        manualEmails.add(r.email.toLowerCase());
      }
    });
    const totalUsers = new Set<string>([...purchaseEmails, ...manualEmails]).size;

    const pdfCounts = new Map<string, number>();
    (pdfRows.data ?? []).forEach((r: { destination: string | null }) => {
      const key = (r.destination as string | null) ?? "(unknown)";
      pdfCounts.set(key, (pdfCounts.get(key) ?? 0) + 1);
    });
    const topPdfGuides = [...pdfCounts.entries()]
      .map(([destination, downloads]) => ({ destination, downloads }))
      .sort((a, b) => b.downloads - a.downloads)
      .slice(0, 10);

    return {
      totalUsers: totalUsers || (convCount.count ?? 0),
      premiumUsers: purchaseEmails.size,
      passUsers: passEmails.size,
      manualAccessUsers: manualEmails.size,
      activeListeners: listenerRows.count ?? 0,
      topDestinations: (dest.data ?? []) as MayaOwnerMetrics["topDestinations"],
      topPdfGuides,
      revenueCents: Number(revenue.total_revenue_cents ?? 0),
    };
  });

// ────────────────────────────────────────────────────────────────────────────
// Admin: user search (joins auth.users with grants + purchases)
// ────────────────────────────────────────────────────────────────────────────
export interface MayaUserSearchRow {
  userId: string | null;
  email: string;
  hasPass: boolean;
  destinations: string[];
  grants: Array<{
    id: string;
    accessType: AccessType;
    destinationKeys: string[];
    expiresAt: string | null;
    revokedAt: string | null;
    createdAt: string;
  }>;
  purchases: Array<{
    id: string;
    productId: string;
    destinationKey: string | null;
    amount: number | null;
    currency: string | null;
    createdAt: string;
  }>;
  isOwner: boolean;
}

export const searchMayaUsers = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ query: z.string().trim().min(1).max(200) }).parse(d),
  )
  .handler(async ({ data, context }): Promise<MayaUserSearchRow[]> => {
    await assertOwnerOrAdmin(context as never);
    const q = data.query.toLowerCase();
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Resolve candidate emails via Auth Admin API and purchases/grants tables.
    const candidateEmails = new Set<string>();
    const userByEmail = new Map<string, { id: string; email: string }>();

    // 1. Auth users (paginated, limited to first page for simplicity)
    try {
      const { data: list } = await supabaseAdmin.auth.admin.listUsers({
        page: 1,
        perPage: 200,
      });
      list?.users.forEach((u) => {
        const e = (u.email ?? "").toLowerCase();
        if (!e) return;
        if (
          e.includes(q) ||
          u.id === q ||
          (u.user_metadata?.name as string | undefined)?.toLowerCase().includes(q)
        ) {
          candidateEmails.add(e);
          userByEmail.set(e, { id: u.id, email: e });
        }
      });
    } catch (err) {
      console.warn("[maya-search] auth.admin.listUsers failed", err);
    }

    // 2. Grants matching email
    const { data: grantMatches } = await supabaseAdmin
      .from("maya_access_grants")
      .select("email")
      .ilike("email", `%${q}%`)
      .limit(50);
    grantMatches?.forEach((g) => g.email && candidateEmails.add(g.email.toLowerCase()));

    // 3. Purchases matching email
    const { data: purchaseMatches } = await supabaseAdmin
      .from("maya_purchases")
      .select("customer_email")
      .ilike("customer_email", `%${q}%`)
      .limit(50);
    purchaseMatches?.forEach(
      (p) => p.customer_email && candidateEmails.add(p.customer_email.toLowerCase()),
    );

    if (!candidateEmails.size) return [];
    const emailsArr = [...candidateEmails].slice(0, 50);

    // Bulk fetch grants + purchases for those emails
    const [{ data: grants }, { data: purchases }] = await Promise.all([
      supabaseAdmin
        .from("maya_access_grants")
        .select(
          "id, email, access_type, destination_keys, expires_at, revoked_at, created_at",
        )
        .in("email", emailsArr),
      supabaseAdmin
        .from("maya_purchases")
        .select("id, customer_email, product_id, destination_key, amount, currency, created_at, status")
        .in("customer_email", emailsArr.map((e) => e))
        .eq("status", "paid"),
    ]);

    // Owner check: which users are owners?
    const userIds = [...userByEmail.values()].map((u) => u.id);
    let ownerIds = new Set<string>();
    if (userIds.length) {
      const { data: roles } = await supabaseAdmin
        .from("user_roles")
        .select("user_id, role")
        .in("user_id", userIds)
        .eq("role", "owner");
      ownerIds = new Set((roles ?? []).map((r) => r.user_id as string));
    }

    return emailsArr.map((email) => {
      const auth = userByEmail.get(email) ?? null;
      const userGrants = (grants ?? [])
        .filter((g) => g.email?.toLowerCase() === email)
        .map((g) => ({
          id: g.id as string,
          accessType: g.access_type as AccessType,
          destinationKeys: (g.destination_keys ?? []) as string[],
          expiresAt: (g.expires_at as string | null) ?? null,
          revokedAt: (g.revoked_at as string | null) ?? null,
          createdAt: g.created_at as string,
        }));
      const userPurchases = (purchases ?? [])
        .filter((p) => p.customer_email?.toLowerCase() === email)
        .map((p) => ({
          id: p.id as string,
          productId: p.product_id as string,
          destinationKey: (p.destination_key as string | null) ?? null,
          amount: (p.amount as number | null) ?? null,
          currency: (p.currency as string | null) ?? null,
          createdAt: p.created_at as string,
        }));
      const dests = new Set<string>();
      userGrants.forEach((g) => {
        if (g.revokedAt) return;
        if (g.expiresAt && new Date(g.expiresAt) <= new Date()) return;
        g.destinationKeys.forEach((d) => dests.add(d));
      });
      userPurchases.forEach((p) => {
        if (p.productId === "maya_destination_one_time" && p.destinationKey)
          dests.add(p.destinationKey);
      });
      const hasPass =
        userPurchases.some((p) => p.productId === "maya_pass_one_time") ||
        userGrants.some(
          (g) =>
            !g.revokedAt &&
            (!g.expiresAt || new Date(g.expiresAt) > new Date()) &&
            (g.accessType === "pass" ||
              g.accessType === "unlimited" ||
              g.accessType === "lifetime"),
        );
      return {
        userId: auth?.id ?? null,
        email,
        hasPass,
        destinations: [...dests],
        grants: userGrants,
        purchases: userPurchases,
        isOwner: auth ? ownerIds.has(auth.id) : false,
      };
    });
  });

// Convenience check used by the UI to decide whether to show the admin nav link.
export const isMayaOwnerOrAdmin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ allowed: boolean; isOwner: boolean }> => {
    const isOwner = await userHasRole(context.supabase, context.userId, "owner");
    const isAdmin =
      isOwner ||
      (await userHasRole(context.supabase, context.userId, "admin"));
    return { allowed: isAdmin, isOwner };
  });
